import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Company } from '../types/database';

/**
 * Company member relationship from Supabase
 */
interface CompanyMemberRelation {
  company_id: string;
  companies: Company;
}

/**
 * useCompanies - Custom hook for managing companies
 * 
 * Fetches companies where user is owner or member with accepted invitation.
 * Provides CRUD operations for companies.
 * 
 * @param userId - The authenticated user ID
 * @returns Companies data, loading state, error state, and CRUD operations
 * 
 * @example
 * ```tsx
 * const { companies, loading, createCompany, updateCompany } = useCompanies(user?.id);
 * 
 * const handleCreate = async () => {
 *   await createCompany({ name: 'Nova Empresa', cnpj: '12345678901234' });
 * };
 * ```
 */
export function useCompanies(userId: string | undefined) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCompanies = useCallback(async () => {
    if (!userId) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch companies where user is owner
      const { data: ownedCompanies, error: ownedError } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (ownedError) throw ownedError;

      // Fetch companies where user is a member
      const { data: memberCompanies, error: memberError } = await supabase
        .from('company_users')
        .select('company_id, companies(*)')
        .eq('user_id', userId)
        .not('accepted_at', 'is', null);

      if (memberError) throw memberError;

      // Combine and deduplicate
      const allCompanies = [
        ...(ownedCompanies || []),
        ...(memberCompanies?.map((mc: CompanyMemberRelation) => mc.companies).filter(Boolean) || []),
      ];

      // Remove duplicates based on ID
      const uniqueCompanies = allCompanies.filter(
        (company, index, self) =>
          index === self.findIndex((c) => c.id === company.id)
      );

      setCompanies(uniqueCompanies);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const createCompany = async (company: Omit<Company, 'id' | 'created_at' | 'updated_at'>) => {
    if (!userId) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('companies')
      .insert([{ ...company, user_id: userId }])
      .select()
      .single();

    if (error) throw error;

    // Add user as owner in company_users (use upsert to avoid conflicts)
    const { error: memberError } = await supabase
      .from('company_users')
      .upsert([
        {
          company_id: data.id,
          user_id: userId,
          role: 'owner',
          accepted_at: new Date().toISOString(),
        },
      ], {
        onConflict: 'company_id,user_id',
        ignoreDuplicates: false,
      });

    // Only throw error if it's not a duplicate key issue
    if (memberError && !memberError.message?.includes('duplicate')) {
      console.error('Error adding user to company:', memberError);
      // Don't throw - company was created successfully
    }

    await fetchCompanies();
    return data;
  };

  const updateCompany = async (id: string, updates: Partial<Company>) => {
    console.log('[useCompanies] updateCompany called:', { id, updates });
    
    const { data, error } = await supabase
      .from('companies')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[useCompanies] Update error:', error);
      throw error;
    }

    console.log('[useCompanies] Update successful, data returned:', data);
    console.log('[useCompanies] Fetching updated companies list...');
    
    await fetchCompanies();
    
    console.log('[useCompanies] Companies refetched');
    return data;
  };

  const deleteCompany = async (id: string) => {
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', id);

    if (error) throw error;

    await fetchCompanies();
  };

  return {
    companies,
    loading,
    error,
    createCompany,
    updateCompany,
    deleteCompany,
    refetch: fetchCompanies,
  };
}
